import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { RegistrationFormData } from '../../types/auth';
import './RegistrationForm.css';

// Validation schema using Yup
const registrationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  name: Yup.string()
    .min(2, 'Name must be at least 2 characters long')
    .max(50, 'Name must be less than 50 characters')
    .required('Name is required'),
  phoneNumber: Yup.string()
    .matches(
      /^\+?[1-9]\d{1,14}$/,
      'Please enter a valid phone number (e.g., +1234567890)'
    )
    .optional()
});

// Initial form values
const initialValues: RegistrationFormData & { confirmPassword: string } = {
  email: '',
  password: '',
  confirmPassword: '',
  name: '',
  phoneNumber: ''
};

interface RegistrationFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ 
  onSuccess, 
  onSwitchToLogin 
}) => {
  const { signUp, isLoading, error, clearError } = useAuth();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (
    values: RegistrationFormData & { confirmPassword: string },
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      clearError();
      setSuccessMessage(null);

      // Remove confirmPassword from the data sent to signUp
      const { confirmPassword, ...registrationData } = values;
      
      const result = await signUp(registrationData);
      
      if (result.success) {
        setSuccessMessage(result.message || 'Registration successful! Please check your email to verify your account.');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000); // Give user time to read success message
        }
      } else {
        // Handle specific field errors if needed
        if (result.message?.includes('email')) {
          setFieldError('email', result.message);
        } else if (result.message?.includes('password')) {
          setFieldError('password', result.message);
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="registration-form-container">
      <div className="registration-form-card">
        <div className="form-header">
          <h2>Create Your Account</h2>
          <p>Join our speech development community and start improving today!</p>
        </div>

        {successMessage && (
          <div className="success-message" role="alert">
            <div className="success-icon">✓</div>
            <div className="success-text">{successMessage}</div>
          </div>
        )}

        {error && (
          <div className="error-message" role="alert">
            <div className="error-icon">⚠</div>
            <div className="error-text">{error}</div>
          </div>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={registrationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form className="registration-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name *
                </label>
                <Field
                  type="text"
                  id="name"
                  name="name"
                  className={`form-input ${touched.name && errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="name" component="div" className="field-error" />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
                  placeholder="Enter your email address"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="email" component="div" className="field-error" />
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber" className="form-label">
                  Phone Number (Optional)
                </label>
                <Field
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  className={`form-input ${touched.phoneNumber && errors.phoneNumber ? 'error' : ''}`}
                  placeholder="Enter your phone number (e.g., +1234567890)"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="phoneNumber" component="div" className="field-error" />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password *
                </label>
                <Field
                  type="password"
                  id="password"
                  name="password"
                  className={`form-input ${touched.password && errors.password ? 'error' : ''}`}
                  placeholder="Create a strong password"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="password" component="div" className="field-error" />
                <div className="password-requirements">
                  <small>
                    Password must contain at least 8 characters with uppercase, lowercase, number, and special character.
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password *
                </label>
                <Field
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className={`form-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  disabled={isLoading || isSubmitting}
                />
                <ErrorMessage name="confirmPassword" component="div" className="field-error" />
              </div>

              <button
                type="submit"
                className={`submit-button ${isLoading || isSubmitting ? 'loading' : ''}`}
                disabled={isLoading || isSubmitting}
              >
                {isLoading || isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </Form>
          )}
        </Formik>

        <div className="form-footer">
          <p>
            Already have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onSwitchToLogin}
              disabled={isLoading}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;